import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Modal from '../../../components/modal/modal.component';
import {IonAlert, useIonViewWillLeave} from '@ionic/react';
import moment, {Moment} from "moment/moment";
import {Order} from "../reportPage.component";
import {ReportSettingScannerMode} from "../../../enums/report";
import {
    checkCameraPermission,
    openSettings,
    startContinuousScan,
    stopAllScanning,
    makeWebViewHidden,
    restoreWebView,
    toggleTorch,
    BARCODE_1D_AND_QR_FORMATS,
    ContinuousScanHandle,
} from "@services/barcodeScanner.service";
import {StyledInput} from "../../../components/form/input/input.style";
import {StyledButton} from "../../../components/button/button.style";

type QrScannerModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (content: string) => void;
    order?: Order;
    logisticContainer?: boolean;
    prefixes?: string[] | null | undefined;
    scannerMode?: ReportSettingScannerMode | null;
};

const QrScannerModal: React.FC<QrScannerModalProps> = (props: QrScannerModalProps) => {

    const {t} = useTranslation();
    const [showComments, updateShowComments] = useState<boolean>(false);
    const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);
    const [scanStartAt, setScanStartAt] = useState<Moment | undefined>();
    const [scanIntervalAt, setScanIntervalAt] = useState<Moment | undefined>();
    const [isManualMode, setIsManualMode] = useState(false);
    const [manualValue, setManualValue] = useState('');

    const scanHandleRef = useRef<ContinuousScanHandle | null>(null);

    const handleClose = () => {
        props.onClose();
        stopScan();
    };

    useEffect(() => {
        handleOpen();
    }, [props.isOpen]);

    const handleOpen = () => {
        updateShowComments(props.isOpen);
        if (props.isOpen) {
            scanComment();
            setScanStartAt(moment());
            setScanIntervalAt(moment());
        }
    };

    const onSubmit = async (content: string) => {
        props.onSubmit(content);
    };

    useIonViewWillLeave(() => {
        stopScan();
    });

    let scanTimeout: NodeJS.Timeout | undefined;
    useEffect(() => {
        checkScanInterval();
        if (scanTimeout) clearInterval(scanTimeout);

        if (scanStartAt) {
            scanTimeout = setInterval(checkScanInterval, 1000);
        }

        return () => {
            if (scanTimeout) clearInterval(scanTimeout);
        };
    }, [scanIntervalAt])

    const checkScanInterval = () => {
        let now = moment();
        if (scanStartAt && now.diff(scanStartAt, 's') > (props.order?.ep2_scanner_timeout ? parseInt(props.order.ep2_scanner_timeout) : 15)) {
            stopScan();
        }
    }

    const startScan = async () => {
        makeWebViewHidden();
        const formats = props.logisticContainer ? BARCODE_1D_AND_QR_FORMATS : [];

        const handle = await startContinuousScan(formats, (rawValue) => {
            if (rawValue) {
                if (props.logisticContainer) {
                    if (props.order?.scanning_settings.logisticContainerUsePrefix || props.prefixes) {
                        let labelName = rawValue.charAt(0);

                        if (props.order?.scanning_settings.logisticContainerPrefix?.includes(labelName) || props.prefixes?.includes(labelName)) {
                            onSubmit(rawValue);
                            stopScan();
                        } else {
                          onSubmit('');
                          stopScan();
                        }
                    } else {
                        onSubmit(rawValue);
                        stopScan()
                    }
                } else {
                    let scannedValue = rawValue;
                    let labelName = scannedValue.charAt(0);

                    if (props.scannerMode === ReportSettingScannerMode.SCANNER_MODE_SKIP_PREFIX) {
                        if (props.prefixes?.includes(labelName)) {
                            scannedValue = scannedValue.substring(1);
                        }
                        onSubmit(scannedValue);
                    } else if (props.prefixes) {
                        if (props.prefixes?.includes(labelName)) {
                          onSubmit(scannedValue);
                        } else {
                          onSubmit('');
                        }
                    } else {
                        onSubmit(scannedValue);
                    }
                    stopScan();
                }
            }
        }, { enableTorch: props.order?.scanning_settings.torchDefault ?? false });

        scanHandleRef.current = handle;
    };

    const deactivateScanner = async () => {
        await scanHandleRef.current?.stop();
        scanHandleRef.current = null;
        await stopAllScanning();
        restoreWebView();
        setScanStartAt(undefined);
        setScanIntervalAt(undefined);
    };

    const stopScan = async () => {
        await deactivateScanner();
        props.onClose();
    };

    const handleCloseButtonClick = async () => {
        if (props.order?.enable_scanner_close_button) {
            await deactivateScanner();
            setIsManualMode(true);
        } else {
            handleClose();
        }
    };

    const handleManualSubmit = () => {
        if (manualValue) {
            props.onSubmit(manualValue);
        }
    };

    const scanComment = async () => {
        let hasPermission = await checkCameraPermission();
        if (!hasPermission) {
            setShowPermissionAlert(true);
        }
        startScan();
    }

    return (
        <Modal
            isOpen={showComments}
            disableClose={false}
            onDidDismiss={() => handleClose()}>

            {isManualMode ? (
                <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <StyledInput
                        type="text"
                        autoFocus
                        value={manualValue}
                        onChange={(e) => setManualValue(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') handleManualSubmit(); }}
                        placeholder={t('reportPage.reportForm.enter')}
                    />
                    <StyledButton onClick={handleManualSubmit}>
                        {t('common.confirm')}
                    </StyledButton>
                </div>
            ) : showComments && (
                <>
                    <div className="scanner-close-button">
                        <button onClick={handleCloseButtonClick}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>
                    <div className="scanner-torch-button">
                        <button onClick={() => toggleTorch()}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="white">
                                <path d="M6 2h12l-2 6h2l-6 10v-6H8l2-6H6z"/>
                            </svg>
                        </button>
                    </div>
                </>
            )}

            <IonAlert
                isOpen={showPermissionAlert}
                onDidDismiss={() => setShowPermissionAlert(false)}
                header={t('common.permissions.alert')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowPermissionAlert(false);
                        },
                    },
                    {
                        text: t('common.permissions.goToAppSettings'),
                        handler: async () => {
                            setShowPermissionAlert(false);
                            openSettings();
                        }
                    }
                ]}
            />
        </Modal>
    );
};

export default QrScannerModal;
